// Notification.jsx
import React, { useState } from 'react';
import Sidebar from '../components/sidebar';
import Header from '../components/header';
import {
    FaBell,
    FaCheckCircle,
    FaTimesCircle,
    FaInfoCircle,
    FaExclamationTriangle,
    FaClock,
    FaEye,
    FaTimes
} from 'react-icons/fa';

function Notification() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('notification');
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    /*
    // Simple dummy notification data
    const notifications = [
        {
            id: 1,
            title: 'Payment Successful',
            description: 'Your payment of ₹2,500 for utility bill has been successfully processed. Your transaction ID is TXN123456.',
            date: '16 July 2026, 10:30 AM',
            icon: <FaCheckCircle className="text-green-500 text-xl" />,
            bgColor: 'bg-green-50'
        },
        {
            id: 2,
            title: 'Recharge Completed',
            description: 'Your mobile recharge of ₹199 for number 9876543210 has been completed successfully.',
            date: '16 July 2026, 09:15 AM',
            icon: <FaInfoCircle className="text-blue-500 text-xl" />,
            bgColor: 'bg-blue-50'
        },
        {
            id: 3,
            title: 'Transaction Failed',
            description: 'Your transaction of ₹500 failed due to insufficient balance. Please try again with sufficient funds.',
            date: '15 July 2026, 06:45 PM',
            icon: <FaTimesCircle className="text-red-500 text-xl" />,
            bgColor: 'bg-red-50'
        },
        {
            id: 4,
            title: 'New Offer Available',
            description: 'Get 10% cashback on all bill payments made through the app. Offer valid till July 31st.',
            date: '15 July 2026, 02:20 PM',
            icon: <FaExclamationTriangle className="text-yellow-500 text-xl" />,
            bgColor: 'bg-yellow-50'
        },
        {
            id: 5,
            title: 'Wallet Credited',
            description: '₹1,000 has been credited to your wallet from referral bonus. Your new balance is ₹5,000.',
            date: '15 July 2026, 11:00 AM',
            icon: <FaCheckCircle className="text-green-500 text-xl" />,
            bgColor: 'bg-green-50'
        },
        {
            id: 6,
            title: 'Bill Reminder',
            description: 'Your electricity bill of ₹1,200 is due tomorrow. Please make the payment to avoid late fees.',
            date: '14 July 2026, 08:00 AM',
            icon: <FaExclamationTriangle className="text-orange-500 text-xl" />,
            bgColor: 'bg-orange-50'
        }
    ];
    */

    const notifications = [];

    // Truncate description to 100 characters
    const truncateText = (text, maxLength = 100) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Open modal with selected notification
    const openModal = (notification) => {
        setSelectedNotification(notification);
        setIsModalOpen(true);
    };

    // Close modal
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedNotification(null);
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">Notification</h1>
                        <p className="text-gray-600 text-sm">View new updated data</p>
                    </div>

                    {/* Notifications List */}
                    <div className="space-y-3">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`${notification.bgColor} rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow`}
                            >
                                <div className="flex items-start gap-3">
                                    {/* Icon */}
                                    <div className="flex-shrink-0 mt-0.5">
                                        {notification.icon}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-semibold text-gray-900">
                                            {notification.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-0.5">
                                            {truncateText(notification.description)}
                                        </p>
                                        <div className="flex items-center gap-3 mt-2">
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                                <FaClock className="text-gray-400 text-xs" />
                                                <span>{notification.date}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* View Button */}
                                    <button
                                        onClick={() => openModal(notification)}
                                        className="flex-shrink-0 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-1.5"
                                    >
                                        <FaEye className="text-xs" />
                                        View
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Empty State (if no notifications) */}
                    {notifications.length === 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                            <div className="flex flex-col items-center">
                                <div className="bg-gray-100 rounded-full p-4 mb-4">
                                    <FaBell className="text-gray-400 text-3xl" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-700">No Notifications</h3>
                                <p className="text-sm text-gray-500">You're all caught up!</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal - Fade Effect */}
            {isModalOpen && selectedNotification && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white rounded-xl max-w-md w-full shadow-2xl animate-scaleIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${selectedNotification.bgColor}`}>
                                    {selectedNotification.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {selectedNotification.title}
                                </h3>
                            </div>
                            <button
                                onClick={closeModal}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FaTimes className="text-gray-500 text-lg" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <p className="text-gray-700 text-base leading-relaxed">
                                {selectedNotification.description}
                            </p>
                            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                                <FaClock className="text-gray-400" />
                                <span>{selectedNotification.date}</span>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex justify-end p-6 border-t border-gray-200">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS for animations */}
            <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
        </div>
    );
}

export default Notification;